/**
 * Reading boolean switches out of the environment.
 *
 * Every `REACT_APP_*` value is a string by the time it reaches the bundle —
 * Create React App inlines them verbatim — so the obvious `!!process.env.FLAG`
 * is wrong in a way that is easy to miss: `!!"false"` is `true`, as is
 * `!!"0"` and `!!"no"`. A switch written that way turns itself ON when someone
 * sets it to the value that means OFF, which is the exact opposite of what
 * whoever edited the config intended.
 *
 * It also matters that the check be explicit rather than "any non-empty value
 * is on". These flags arrive through several layers — a GitHub secret, the
 * `echo NAME=${{ secrets.NAME }} >> .env` step in the deploy workflow, then
 * dotenv — and an unset secret produces the empty string rather than nothing
 * at all. Only the literal `true` should count.
 */
export const isEnvFlagEnabled = (value: string | undefined): boolean => (value ?? "").trim().toLowerCase() === "true";

/**
 * Whether the signup flow skips phone verification entirely.
 *
 * When this is on, the phone step accepts the number and moves straight to the
 * next step: no `users/phone/verify_request` call is made, no code is sent, no
 * code is asked for, and the SMS verification screen is not reachable. It is a
 * deliberate product decision rather than a fallback for the SMS provider
 * being down — nothing re-enables itself if delivery starts working again.
 *
 * Read through this helper in every call site so the flag cannot mean one
 * thing in one flow and something else in another.
 */
export const isSmsVerificationDisabled = (): boolean =>
  isEnvFlagEnabled(process.env.REACT_APP_DISABLE_SMS_VERIFICATION);
